import React, { useState, useEffect, useCallback } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './AdminPerformanceView.css';
import { scopeForAdmin, adminSchoolIds, docInScope } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

// Normalize a grade doc to a 0-100 percentage (handles string/number scores and
// docs that only carry a precomputed `percentage`).
const gradePercent = (g) => {
  const score = Number(g.score);
  const maxScore = Number(g.maxScore);
  if (Number.isFinite(score) && Number.isFinite(maxScore) && maxScore > 0) {
    return (score / maxScore) * 100;
  }
  const pct = Number(g.percentage);
  return Number.isFinite(pct) ? pct : null;
};

function AdminPerformanceView({ adminData }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [classesLoaded, setClassesLoaded] = useState(false);

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminPerformance: Loading classes, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      classesData = scopeForAdmin(classesData, adminData);
      console.log('🔥 AdminPerformance: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminPerformance: Error loading classes:', error);
    } finally {
      setClassesLoaded(true);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminPerformance: Loading students');
    try {
      const studentsData = [];
      const perClass = await Promise.all(classes.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      classes.forEach((cls, i) => perClass[i].forEach(data => {
        if (data.isActive === false) return;
        studentsData.push({ ...data, classId: data.classId || cls.id });
      }));
      console.log('🔥 AdminPerformance: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminPerformance: Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  const loadGrades = useCallback(async () => {
    console.log('🔥 AdminPerformance: Loading grades');
    try {
      const schoolIds = adminSchoolIds(adminData);
      const studentIds = new Set(students.flatMap(s => [s.id, s.studentId].filter(Boolean)));
      const gradesByKey = new Map();
      const assignmentToClass = {};
      const pushGrade = (docId, data, fallbackClassId) => {
        const key = `${data.studentId || ''}|${data.assignmentId || docId}`;
        if (gradesByKey.has(key)) return;
        gradesByKey.set(key, { ...data, id: docId, classId: data.classId || fallbackClassId || null });
      };

      // Primary store: classes/{cid}/assignments/{aid}/grades (parallel, cached)
      await Promise.all(classes.map(async (cls) => {
        const assignmentDocs = await dataCacheService.getDocs(ck(`classes/${cls.id}/assignments`), collection(db, 'classes', cls.id, 'assignments'));
        const gradesPerAssignment = await Promise.all(assignmentDocs.map(a =>
          dataCacheService.getDocs(ck(`classes/${cls.id}/assignments/${a.id}/grades`), collection(db, 'classes', cls.id, 'assignments', a.id, 'grades'))
        ));
        assignmentDocs.forEach((a, i) => {
          assignmentToClass[a.id] = cls.id;
          gradesPerAssignment[i].forEach(g => pushGrade(g.id, g, cls.id));
        });
        // Legacy class-level collection: classes/{cid}/grades
        const classGradesDocs = await dataCacheService.getDocs(ck(`classes/${cls.id}/grades`), collection(db, 'classes', cls.id, 'grades'));
        classGradesDocs.forEach(g => pushGrade(g.id, g, cls.id));
      }));

      // Top-level `grades` collection (iOS batch writes — no classId field)
      try {
        const topGradesDocs = await dataCacheService.getDocs(ck('grades'), collection(db, 'grades'));
        topGradesDocs.forEach(data => {
          const inScope = docInScope(data.schoolId, schoolIds)
            || classes.some(c => c.id === assignmentToClass[data.assignmentId])
            || studentIds.has(data.studentId);
          if (inScope) pushGrade(data.id, data, assignmentToClass[data.assignmentId]);
        });
      } catch (e) {
        console.error('🔥 AdminPerformance: top-level grades query failed (non-fatal):', e);
      }

      const gradesData = [...gradesByKey.values()];
      console.log('🔥 AdminPerformance: Grades loaded', gradesData.length, gradesData);
      setGrades(gradesData);
    } catch (error) {
      console.error('🔥 AdminPerformance: Error loading grades:', error);
    }
  }, [classes, students, adminData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (!classesLoaded) return;
    if (classes.length > 0) {
      loadStudents();
    } else {
      setLoading(false);
    }
  }, [classesLoaded, classes, loadStudents]);

  useEffect(() => {
    if (classesLoaded) {
      loadGrades();
    }
  }, [classesLoaded, classes, students, loadGrades]);

  const getFilteredStudents = () => {
    if (selectedClass === 'all') return students;
    return students.filter(student => student.classId === selectedClass);
  };

  // grade.studentId may be the student's Firestore doc id or the school-issued
  // studentId code — match on both.
  const gradesForStudent = (student) =>
    grades.filter(g => g.studentId === student.id || (!!student.studentId && g.studentId === student.studentId));

  const calculateStudentAverage = (student) => {
    const pcts = gradesForStudent(student).map(gradePercent).filter(p => p !== null);
    if (pcts.length === 0) return 0;
    const sum = pcts.reduce((acc, p) => acc + p, 0);
    return parseFloat((sum / pcts.length).toFixed(1));
  };

  const getPerformanceTier = (average) => {
    if (average >= 90) return { tier: 'Excellent', color: '#22c55e', letter: 'A' };
    if (average >= 80) return { tier: 'Good', color: '#22c55e', letter: 'B' };
    if (average >= 70) return { tier: 'Satisfactory', color: '#f59e0b', letter: 'C' };
    if (average >= 60) return { tier: 'Needs Improvement', color: '#f97316', letter: 'D' };
    return { tier: 'Critical', color: '#ef4444', letter: 'F' };
  };

  const getPerformanceDistribution = (studentsWithPerf) => {
    const tiers = { Excellent: 0, Good: 0, Satisfactory: 0, 'Needs Improvement': 0, Critical: 0 };
    studentsWithPerf.forEach(s => {
      const tier = s.performance.tier;
      if (tiers[tier] !== undefined) tiers[tier]++;
    });
    return [
      { name: 'Excellent (90+)', value: tiers.Excellent, color: '#22c55e' },
      { name: 'Good (80-89)', value: tiers.Good, color: '#22c55e' },
      { name: 'Satisfactory (70-79)', value: tiers.Satisfactory, color: '#f59e0b' },
      { name: 'Needs Improvement (60-69)', value: tiers['Needs Improvement'], color: '#f97316' },
      { name: 'Critical (<60)', value: tiers.Critical, color: '#ef4444' },
    ].filter(d => d.value > 0);
  };

  const getClassAverages = () => {
    return classes.map(cls => {
      const classStudents = students.filter(s => s.classId === cls.id);
      if (classStudents.length === 0) return { name: cls.name, average: 0, students: 0 };
      const averages = classStudents.map(s => calculateStudentAverage(s));
      const classAvg = averages.reduce((a, b) => a + b, 0) / averages.length;
      return { name: cls.name, average: parseFloat(classAvg.toFixed(1)), students: classStudents.length };
    }).filter(c => c.students > 0).sort((a, b) => b.average - a.average);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredStudents = getFilteredStudents();
  const studentsWithPerformance = filteredStudents.map(student => {
    const average = calculateStudentAverage(student);
    const performance = getPerformanceTier(average);
    const studentGrades = gradesForStudent(student);
    return { ...student, average, performance, studentGrades };
  }).sort((a, b) => b.average - a.average);

  return (
    <div className="admin-performance-view">
      <div className="view-header">
        <h2>Performance Analytics</h2>
      </div>

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <div className="stats-section">
        <h3>Performance Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>{studentsWithPerformance.length}</h3>
              <p>Total Students</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{studentsWithPerformance.filter(s => s.average >= 70).length}</h3>
              <p>Performing Well</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚠️</div>
            <div className="stat-info">
              <h3>{studentsWithPerformance.filter(s => s.average < 70).length}</h3>
              <p>Needs Attention</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {studentsWithPerformance.length > 0 && (
        <div className="charts-section">
          {/* Performance Distribution Pie Chart */}
          <div className="chart-card">
            <h3>Performance Distribution</h3>
            {getPerformanceDistribution(studentsWithPerformance).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={getPerformanceDistribution(studentsWithPerformance)}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                   
                  >
                    {getPerformanceDistribution(studentsWithPerformance).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">No grade data yet</div>
            )}
          </div>

          {/* Class Averages Bar Chart */}
          <div className="chart-card">
            <h3>Class Averages Comparison</h3>
            {getClassAverages().length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getClassAverages()} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" height={70} interval={0} tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} label={{ value: 'Average %', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="average" fill="#22c55e" radius={[4, 4, 0, 0]} name="Class Average %" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-empty">No class averages yet</div>
            )}
          </div>
        </div>
      )}

      {studentsWithPerformance.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Performance Data</h3>
          <p>No students found or no grades recorded</p>
        </div>
      ) : (
        <div className="performance-list">
          {studentsWithPerformance.map(student => (
            <div key={student.id} className="performance-card" onClick={() => { setSelectedStudent(student); setShowDetailModal(true); }}>
              <div className="student-avatar">
                {student.firstName ? student.firstName[0] : 'S'}
              </div>
              <div className="performance-info">
                <h4>{student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown'}</h4>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {classes.find(c => c.id === student.classId)?.name || 'N/A'}</p>
              </div>
              <div className="performance-metrics">
                <div className="average-score">
                  <span className="score-value">{student.average}%</span>
                  <span className="score-label">Average</span>
                </div>
                <div className="performance-tier" style={{ background: `${student.performance.color}20`, color: student.performance.color }}>
                  {student.performance.tier}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDetailModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Student Performance Details</h3>
              <button className="close-button" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="student-detail-header">
                <div className="student-avatar-large">
                  {selectedStudent.firstName ? selectedStudent.firstName[0] : 'S'}
                </div>
                <div className="student-detail-info">
                  <h4>{selectedStudent.fullName || `${selectedStudent.firstName || ''} ${selectedStudent.lastName || ''}`.trim() || 'Unknown'}</h4>
                  <p>Student ID: {selectedStudent.studentId}</p>
                  <p>Class: {classes.find(c => c.id === selectedStudent.classId)?.name || 'N/A'}</p>
                </div>
              </div>
              <div className="performance-summary">
                <div className="summary-card">
                  <span className="summary-label">Average</span>
                  <span className="summary-value" style={{ color: selectedStudent.performance.color }}>{selectedStudent.average}%</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Performance</span>
                  <span className="summary-value" style={{ color: selectedStudent.performance.color }}>{selectedStudent.performance.tier}</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">Total Grades</span>
                  <span className="summary-value">{selectedStudent.studentGrades.length}</span>
                </div>
              </div>
              <div className="grades-detail">
                <h5>Recent Grades</h5>
                {selectedStudent.studentGrades.length === 0 ? (
                  <p className="no-grades">No grades recorded</p>
                ) : (
                  <div className="grades-list">
                    {selectedStudent.studentGrades.map((grade, index) => {
                      const percentage = gradePercent(grade);
                      return (
                        <div key={index} className="grade-item">
                          <span className="grade-score">{grade.score}/{grade.maxScore}</span>
                          <span className="grade-percentage">{percentage === null ? 'N/A' : `${percentage.toFixed(1)}%`}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPerformanceView;
